import m from 'mithril'

/**
 * Fine Tuning Mode for a returned Optimus job, shared by every tab of the job report.
 *
 * The graph transformer has to be activated before it can score an edit, so the mode warms up before it
 * accepts one, and the button fills while it does — a short wait for now, see WARM_UP_MS. Once the mode is
 * ready the Split Analysis rows become adjustable and the Timing Plan, Performance Prediction and
 * Time-Space Diagram read their numbers back through this model.
 *
 * ── How an edit moves the numbers ────────────────────────────────────────────────────────────────────
 * The transformer is not wired in yet, so the impact is PROJECTED rather than scored. Every projection is
 * the same one move: walk the straight line through the job's own before/after pair and read it off at the
 * fine-tuned value. That line is the only ground truth on hand, and it is exact at both ends — a phase
 * returned to the optimizer's split reads back the optimizer's number.
 *
 * Splits and offsets drive different metrics, and the projections keep them apart:
 *   - a split moves green time, control delay and (through delay) queue length on that phase;
 *   - an offset moves arrivals-on-green and bandwidth on the route it belongs to.
 */

// How long the button fills before the mode accepts an edit. Held short while the transformer is not
// actually being called; put it back to the real activation time (about 60_000) once it is.
export const WARM_UP_MS = 1_500

export type FineTuningStatus = 'off' | 'warming' | 'ready'

const splitKey = (signal_id: string, phase_num: number) => `${signal_id}::${phase_num}`

let warm_up_timer: ReturnType<typeof setTimeout> | null = null

/**
 * Read a metric off the line through (from_x, from_y) and (to_x, to_y) at `x`.
 *
 * Returns null when the two anchors share an x — the job left that value alone, so there is no slope to
 * read and the caller falls back to holding the number rather than inventing a response.
 */
const projectAlong = (from_x: number, from_y: number, to_x: number, to_y: number, x: number): number | null => {
	if (from_x === to_x) return null

	return to_y + ((to_y - from_y) / (to_x - from_x)) * (x - to_x)
}

const OptimusFineTuningModel = {
	status: 'off' as FineTuningStatus,
	/** Set when the mode is closed with Save: the edits stay on the report after the mode itself ends. */
	is_saved: false,
	/** The job the mode was started on — switching jobs drops the edits rather than carrying them over. */
	job_id: null as string | null,
	/** Seconds added to (or taken from) a phase's optimized split, keyed `${signal_id}::${phase_num}`. */
	split_deltas: {} as Record<string, number>,
	/** Seconds added to (or taken from) a signal's optimized offset, keyed by signal id. */
	offset_deltas: {} as Record<string, number>,
	/** When the warm-up started, so the fill picks up where it is on a redraw rather than restarting. */
	warm_up_started_at: null as number | null,

	isActive: (job_id: string): boolean =>
		OptimusFineTuningModel.job_id === job_id && OptimusFineTuningModel.status !== 'off',

	isReady: (job_id: string): boolean =>
		OptimusFineTuningModel.job_id === job_id && OptimusFineTuningModel.status === 'ready',

	/**
	 * Whether the report should be showing the fine-tuned numbers. True while the mode is open, and still
	 * true after it is closed with Save — closing the mode stops the editing, not the edit.
	 */
	isApplied: (job_id: string): boolean =>
		OptimusFineTuningModel.job_id === job_id &&
		OptimusFineTuningModel.hasEdits() &&
		(OptimusFineTuningModel.status === 'ready' || OptimusFineTuningModel.is_saved),

	start: (job_id: string): void => {
		if (OptimusFineTuningModel.job_id !== job_id) OptimusFineTuningModel.reset()

		OptimusFineTuningModel.job_id = job_id
		OptimusFineTuningModel.is_saved = false

		if (warm_up_timer) clearTimeout(warm_up_timer)
		warm_up_timer = null

		if (WARM_UP_MS <= 0) {
			OptimusFineTuningModel.status = 'ready'
			OptimusFineTuningModel.warm_up_started_at = null
			m.redraw()
			return
		}

		OptimusFineTuningModel.status = 'warming'
		OptimusFineTuningModel.warm_up_started_at = Date.now()
		warm_up_timer = setTimeout(() => {
			OptimusFineTuningModel.status = 'ready'
			m.redraw()
		}, WARM_UP_MS)

		m.redraw()
	},

	reset: (): void => {
		if (warm_up_timer) clearTimeout(warm_up_timer)
		warm_up_timer = null
		OptimusFineTuningModel.status = 'off'
		OptimusFineTuningModel.is_saved = false
		OptimusFineTuningModel.job_id = null
		OptimusFineTuningModel.split_deltas = {}
		OptimusFineTuningModel.offset_deltas = {}
		OptimusFineTuningModel.warm_up_started_at = null
	},

	/** Close the mode and keep the edits: the report goes on showing what it was tuned to. */
	save: (): void => {
		if (warm_up_timer) clearTimeout(warm_up_timer)
		warm_up_timer = null
		OptimusFineTuningModel.status = 'off'
		OptimusFineTuningModel.is_saved = true
		OptimusFineTuningModel.warm_up_started_at = null
		m.redraw()
	},

	/** Close the mode and throw the edits away: back to the plan the optimizer returned. */
	discard: (): void => {
		OptimusFineTuningModel.reset()
		m.redraw()
	},

	clearEdits: (): void => {
		OptimusFineTuningModel.split_deltas = {}
		OptimusFineTuningModel.offset_deltas = {}
	},

	hasEdits: (): boolean =>
		Object.keys(OptimusFineTuningModel.split_deltas).length > 0 ||
		Object.keys(OptimusFineTuningModel.offset_deltas).length > 0,

	/** Changes whenever an edit lands, so a view holding derived data knows to rebuild it. */
	editsKey: (): string =>
		`${OptimusFineTuningModel.status}|${OptimusFineTuningModel.is_saved}|${JSON.stringify(OptimusFineTuningModel.split_deltas)}|${JSON.stringify(OptimusFineTuningModel.offset_deltas)}`,

	// ── Splits ───────────────────────────────────────────────────────────────────────────────────────

	splitDelta: (signal_id: string, phase_num: number | string): number =>
		OptimusFineTuningModel.split_deltas[splitKey(signal_id, Number(phase_num))] ?? 0,

	setSplitDelta: (signal_id: string, phase_num: number | string, delta: number): void => {
		const key = splitKey(signal_id, Number(phase_num))
		if (delta === 0) delete OptimusFineTuningModel.split_deltas[key]
		else OptimusFineTuningModel.split_deltas[key] = delta
	},

	/** The split a phase is being fine-tuned to, or the optimizer's own when it has not been touched. */
	adjustedSplit: (signal_id: string, phase_num: number | string, after_split: number | null): number | null =>
		after_split == null ? null : after_split + OptimusFineTuningModel.splitDelta(signal_id, phase_num),

	/** Total seconds moved across a signal's phases — a coordinated plan has to give back what it takes. */
	signalSplitDeltaTotal: (signal_id: string): number => {
		let total = 0
		for (const [key, delta] of Object.entries(OptimusFineTuningModel.split_deltas)) {
			if (key.startsWith(`${signal_id}::`)) total += delta
		}

		return total
	},

	// ── Offsets ──────────────────────────────────────────────────────────────────────────────────────

	offsetDelta: (signal_id: string): number => OptimusFineTuningModel.offset_deltas[signal_id] ?? 0,

	setOffsetDelta: (signal_id: string, delta: number): void => {
		if (delta === 0) delete OptimusFineTuningModel.offset_deltas[signal_id]
		else OptimusFineTuningModel.offset_deltas[signal_id] = delta
	},

	adjustedOffset: (signal_id: string, after_offset: number | null, cycle?: number | null): number | null => {
		if (after_offset == null) return null

		const offset = after_offset + OptimusFineTuningModel.offsetDelta(signal_id)
		if (!cycle || cycle <= 0) return offset

		// An offset is a position in the cycle, so it wraps rather than running past the cycle length.
		return ((offset % cycle) + cycle) % cycle
	},

	// ── Projections ──────────────────────────────────────────────────────────────────────────────────

	/**
	 * A phase's control delay at its fine-tuned split.
	 *
	 * Between the job's own two splits the delay is read straight off the line through them: exact at both
	 * ends, and it carries whatever the optimizer actually measured on that phase.
	 *
	 * Outside that span the line is the wrong shape. Its slope is whatever the optimizer's own move
	 * happened to produce, and much of that move came from the rest of the network rather than from this
	 * phase's split — extended far enough it will hand a phase more green AND more delay, or drive the
	 * delay through zero. Past the measured span the delay is scaled by the split instead, which is the
	 * first-order relation (delay runs with the inverse of the green a phase gets) and stays positive and
	 * monotonic however far the edit goes.
	 */
	projectPhaseDelay: (
		signal_id: string,
		phase_num: number | string,
		before_split: number | null | undefined,
		after_split: number | null | undefined,
		before_delay: number | null | undefined,
		after_delay: number | null | undefined,
	): number | null => {
		if (after_delay == null) return null

		const delta = OptimusFineTuningModel.splitDelta(signal_id, phase_num)
		if (!delta || after_split == null) return after_delay

		const tuned_split = after_split + delta
		if (tuned_split <= 0) return after_delay

		if (before_split != null && before_delay != null) {
			const [low, high] = before_split <= after_split ? [before_split, after_split] : [after_split, before_split]
			if (tuned_split >= low && tuned_split <= high) {
				const projected = projectAlong(before_split, before_delay, after_split, after_delay, tuned_split)
				if (projected !== null) return Math.max(projected, 0)
			}
		}

		return Math.max((after_delay * after_split) / tuned_split, 0)
	},

	/**
	 * Ratio the projection moved a metric by, for the aggregates that ride on it — network queue length
	 * follows control delay rather than carrying a slope of its own.
	 */
	scaleBy: (value: number | null | undefined, from: number | null, to: number | null): number | null => {
		if (value == null) return null
		if (from == null || to == null || from === 0) return value

		return value * (to / from)
	},
}

// ── Projected job outputs ────────────────────────────────────────────────────────────────────────────

/** The slices of an Optimus job the projection reads and rewrites. Everything else is passed through. */
export interface IProjectedPhase {
	split?: number | null
	effective_green_time?: number | null
	control_delay_seconds?: number | null
	[extra: string]: unknown
}

export interface IProjectedSignal {
	cycle?: number | null
	offset?: number | null
	mean_control_delay_seconds?: number | null
	mean_queue_length_meters?: number | null
	phase_data?: Record<string, IProjectedPhase>
	[extra: string]: unknown
}

export interface IProjectedNetworkMetrics {
	network_control_delay_seconds?: number | null
	network_queue_length_meters?: number | null
	network_effective_green_time?: number | null
	[extra: string]: unknown
}


const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0)

/**
 * The job's outputs as the fine-tuned plan would return them.
 *
 * Splits and green times move with the edit second for second; phase control delay is read off the job's
 * own before/after line at the new split; a signal's mean delay and the network's follow their phases; and
 * queue length rides on delay rather than carrying a slope of its own. Arrivals-on-green and bandwidth are
 * left alone — those are the offsets' to move, not the splits'.
 *
 * Nothing is mutated: the signals and phases that changed are rebuilt, the rest is passed through.
 */
export const projectJobOutputs = <TSignal extends IProjectedSignal, TMetrics extends IProjectedNetworkMetrics>(
	job_id: string,
	split_input: Record<string, TSignal> | null | undefined,
	split_output: Record<string, TSignal> | null | undefined,
	network_metrics_output: TMetrics | null | undefined,
): { split_output: Record<string, TSignal>; network_metrics_output: TMetrics } => {
	const outputs = {
		split_output: split_output ?? ({} as Record<string, TSignal>),
		network_metrics_output: network_metrics_output ?? ({} as TMetrics),
	}

	if (!OptimusFineTuningModel.isApplied(job_id) || !split_output) return outputs

	const projected_signals: Record<string, TSignal> = {}
	const signal_delay_deltas: number[] = []
	const green_deltas: number[] = []
	let phase_count = 0

	for (const [signal_id, signal] of Object.entries(split_output)) {
		const before_signal = split_input?.[signal_id]
		const projected_phases: Record<string, IProjectedPhase> = {}
		const phase_delay_deltas: number[] = []
		const signal_phase_count = Object.keys(signal.phase_data ?? {}).length
		phase_count += signal_phase_count

		for (const [phase_num, phase] of Object.entries(signal.phase_data ?? {})) {
			const delta = OptimusFineTuningModel.splitDelta(signal_id, phase_num)
			if (!delta) {
				projected_phases[phase_num] = phase
				continue
			}

			const before_phase = before_signal?.phase_data?.[phase_num]
			const projected_delay = OptimusFineTuningModel.projectPhaseDelay(
				signal_id,
				phase_num,
				before_phase?.split,
				phase.split,
				before_phase?.control_delay_seconds,
				phase.control_delay_seconds,
			)

			if (projected_delay != null && phase.control_delay_seconds != null) {
				phase_delay_deltas.push(projected_delay - phase.control_delay_seconds)
			}
			green_deltas.push(delta)

			// Spreading the phase and overwriting three of its fields keeps its own type; TS cannot see that
			// through the generic, so the shape is asserted rather than widened for every caller.
			projected_phases[phase_num] = {
				...phase,
				split: phase.split == null ? phase.split : phase.split + delta,
				effective_green_time:
					phase.effective_green_time == null ? phase.effective_green_time : phase.effective_green_time + delta,
				control_delay_seconds: projected_delay == null ? phase.control_delay_seconds : Math.max(projected_delay, 0),
			}
		}

		const offset_delta = OptimusFineTuningModel.offsetDelta(signal_id)
		// A signal's mean delay is the mean over ALL its phases, so one edited phase moves it by its own
		// change spread across the whole signal. Averaging over just the edited phases would move the
		// signal by a single phase's full swing.
		const delay_shift = signal_phase_count ? sum(phase_delay_deltas) / signal_phase_count : null

		if (!phase_delay_deltas.length && !offset_delta) {
			projected_signals[signal_id] = { ...signal, phase_data: projected_phases } as TSignal
			continue
		}

		const projected_signal_delay =
			delay_shift === null || signal.mean_control_delay_seconds == null
				? signal.mean_control_delay_seconds
				: Math.max(signal.mean_control_delay_seconds + delay_shift, 0)

		if (delay_shift !== null && signal.mean_control_delay_seconds != null) {
			signal_delay_deltas.push((projected_signal_delay ?? 0) - signal.mean_control_delay_seconds)
		}

		projected_signals[signal_id] = {
			...signal,
			phase_data: projected_phases,
			offset: OptimusFineTuningModel.adjustedOffset(signal_id, signal.offset ?? null, signal.cycle),
			mean_control_delay_seconds: projected_signal_delay,
			mean_queue_length_meters: OptimusFineTuningModel.scaleBy(
				signal.mean_queue_length_meters,
				signal.mean_control_delay_seconds ?? null,
				projected_signal_delay ?? null,
			),
		} as TSignal
	}

	outputs.split_output = projected_signals

	// Same again one level up: the network's numbers are means over every signal and every phase, so an
	// edit lands spread across the whole network rather than at its full size.
	const signal_count = Object.keys(split_output).length
	const network_delay_shift = signal_count ? sum(signal_delay_deltas) / signal_count : null
	const network_green_shift = phase_count ? sum(green_deltas) / phase_count : null
	const network_metrics = outputs.network_metrics_output
	const projected_network_delay =
		network_delay_shift === null || network_metrics.network_control_delay_seconds == null
			? network_metrics.network_control_delay_seconds
			: Math.max(network_metrics.network_control_delay_seconds + network_delay_shift, 0)

	outputs.network_metrics_output = {
		...network_metrics,
		network_control_delay_seconds: projected_network_delay,
		network_queue_length_meters: OptimusFineTuningModel.scaleBy(
			network_metrics.network_queue_length_meters,
			network_metrics.network_control_delay_seconds ?? null,
			projected_network_delay ?? null,
		),
		network_effective_green_time:
			network_green_shift === null || network_metrics.network_effective_green_time == null
				? network_metrics.network_effective_green_time
				: network_metrics.network_effective_green_time + network_green_shift,
	} as TMetrics

	return outputs
}

export default OptimusFineTuningModel
